import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  apiAdminshopsIdPatch,
  apiAdminshopWorkspacesIdPatch,
  getApiShopsIdGetQueryKey,
  getApiShopsGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type {
  ShopShopUpdateJsonMergePatchModel,
  ShopWorkspaceShopWorkspaceUpdateJsonMergePatchModel
} from '@r/api/generated/client.schemas.ts';

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Button } from '@ui/button.tsx';
import { Save } from 'lucide-react';
import { toDate } from 'date-fns';
import { Checkbox } from '@ui/checkbox.tsx';
import { NumberInput } from '@ui/number-input.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { DatePicker } from '@ui/date-picker.tsx';
import { toUtcStartOfDay, toUtcEndOfDay, getStartOfToday } from '@r/utils/date-utils.ts';
import { cn } from '@r/utils/utils.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useState } from 'react';

export const UpdateShopForm = (shop: {
  id: number;
  name?: string | null;
  displayName: string;
  description: string;
  onSuccess?: () => void;
  shopWorkspace: {
    id: number;
    isActivable?: boolean;
    shopOpeningDate?: string | null;
    shopClosingDate?: string | null;
    minPoint?: number | null;
    coefficient?: number | null;
    isActive?: boolean | null;
  };
}) => {
  const { shopWorkspace, id, displayName, description, onSuccess } = shop;
  const isActivable = shopWorkspace.isActivable;

  const queryClient = useQueryClient();

  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const FormSchema = z.object({
    displayName: z.string().min(1).max(100),
    description: z.string().min(1).max(255),
    shopOpeningDate: z.date().nullable().optional(),
    shopClosingDate: z.date().nullable().optional(),
    minPoint: z.coerce
      .number({ invalid_type_error: t('form.mandatory') })
      .int()
      .min(0),
    coefficient: z.coerce.number().optional(),
    isActive: z.boolean()
  });

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      displayName,
      description,
      shopOpeningDate: shopWorkspace.shopOpeningDate
        ? toDate(shopWorkspace.shopOpeningDate)
        : undefined,
      shopClosingDate: shopWorkspace.shopClosingDate
        ? toDate(shopWorkspace.shopClosingDate)
        : undefined,
      minPoint: shopWorkspace.minPoint ?? 0,
      coefficient: shopWorkspace.coefficient ?? 0,
      isActive: shopWorkspace.isActive ?? false
    },
    shouldUnregister: true
  });

  async function onSubmit(data: Partial<z.infer<typeof FormSchema>>) {
    const shopId = shopWorkspace.id;
    const formDirty = form.formState.dirtyFields;
    setIsSubmitting(true);

    try {
      const shopPayload: ShopShopUpdateJsonMergePatchModel = {
        ...(formDirty.displayName && data.displayName ? { displayName: data.displayName } : {}),
        ...(formDirty.description && data.description ? { description: data.description } : {})
      };

      const workspacePayload: ShopWorkspaceShopWorkspaceUpdateJsonMergePatchModel = {
        shopOpeningDate:
          formDirty.shopOpeningDate && data.shopOpeningDate
            ? toUtcStartOfDay(data.shopOpeningDate).toISOString()
            : undefined,
        shopClosingDate:
          formDirty.shopClosingDate && data.shopClosingDate
            ? toUtcEndOfDay(data.shopClosingDate).toISOString()
            : undefined,
        minPoint: formDirty.minPoint ? (data.minPoint ?? undefined) : undefined,
        coefficient: formDirty.coefficient ? (data.coefficient as number | undefined) : undefined,
        isActive: formDirty.isActive ? (data.isActive ?? undefined) : undefined
      };

      await Promise.all([
        Object.keys(shopPayload).length > 0
          ? apiAdminshopsIdPatch(String(id), shopPayload)
          : Promise.resolve(),
        Object.keys(workspacePayload).some(
          (key) => workspacePayload[key as keyof typeof workspacePayload] !== undefined
        )
          ? apiAdminshopWorkspacesIdPatch(String(shopId), workspacePayload)
          : Promise.resolve()
      ]);

      toast.success(`${t('form.shop.success', { shopName: data.displayName ?? displayName })}`);
      queryClient.invalidateQueries({ queryKey: getApiShopsIdGetQueryKey(id?.toString()) });
      queryClient.invalidateQueries({ queryKey: getApiShopsGetCollectionQueryKey() });
      onSuccess?.();
    } catch (error) {
      showOrvalErrorToast(error, t('form.shop.error'));
    } finally {
      setIsSubmitting(false);
    }
  }

  const shopClosingDate = form.watch('shopClosingDate');
  const shopOpeningDate = form.watch('shopOpeningDate');

  return (
    <Form {...form} schema={FormSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <FormField
          control={form.control}
          name="displayName"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.shops.update.form.display.name.label')}</FormLabel>
              <FormControl>
                <Input maxLength={100} error={!!fieldState.error} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="description"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('admin.shops.update.form.description.label')}</FormLabel>
              <FormControl>
                <Textarea rows={3} maxLength={255} error={!!fieldState.error} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="shopOpeningDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className={!isActivable ? 'opacity-50 cursor-not-allowed' : ''}>
                  {t('label.shop.opening.date')}
                </FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!isActivable}
                    isDateDisabled={(date) =>
                      date < getStartOfToday() || (!!shopClosingDate && date >= shopClosingDate)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="shopClosingDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className={!isActivable ? 'opacity-50 cursor-not-allowed' : ''}>
                  {t('label.shop.closing.date')}
                </FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    disabled={!isActivable}
                    isDateDisabled={(date) =>
                      date < getStartOfToday() || (!!shopOpeningDate && date <= shopOpeningDate)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="minPoint"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel className={!isActivable ? 'opacity-50 cursor-not-allowed' : ''}>
                  {t('form.shop.min.point')}
                </FormLabel>
                <FormControl>
                  <NumberInput
                    placeholder={t('form.shop.min.point')}
                    value={field.value}
                    onValueChange={(value) => {
                      if (isActivable) {
                        field.onChange(value);
                      }
                    }}
                    min={0}
                    disabled={!isActivable}
                    className={!isActivable ? 'opacity-50 cursor-not-allowed' : ''}
                  />
                </FormControl>
                <FormMessage className="mt-1 text-xs" />
              </FormItem>
            )}
          />

          {shopWorkspace.coefficient && (
            <FormField
              control={form.control}
              name="coefficient"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <FormLabel className={!isActivable ? 'opacity-50 cursor-not-allowed' : ''}>
                    {t('form.coefficient.label')}
                  </FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder={t('form.coefficient.label')}
                      value={field.value}
                      onChange={(e) => {
                        if (isActivable) {
                          const value = e.target.value.replace(',', '.');
                          field.onChange(value);
                        }
                      }}
                      disabled={!isActivable}
                      className={cn(
                        'px-2 py-1 border rounded',
                        !isActivable && 'cursor-not-allowed opacity-50'
                      )}
                    />
                  </FormControl>
                  <FormMessage className="mt-1 text-xs" />
                </FormItem>
              )}
            />
          )}
        </div>

        <div className="space-y-2">
          <FormField
            control={form.control}
            name="isActive"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex items-center space-x-2 space-y-1',
                  !isActivable && 'opacity-50 cursor-not-allowed'
                )}
              >
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      if (isActivable) {
                        field.onChange(checked);
                      }
                    }}
                    disabled={!isActivable}
                    className={!isActivable ? 'cursor-not-allowed' : ''}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className={!isActivable ? 'cursor-not-allowed' : ''}>
                    {t('form.shop.active')}
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />
          {!isActivable && (
            <p className="text-xs text-muted-foreground/80">{t('shop.not.activable.message')}</p>
          )}
        </div>

        <Button loading={isSubmitting} className="w-full" type="submit">
          <Save className="mr-2 w-4 h-4" />
          {t('form.save.modifications')}
        </Button>
      </form>
    </Form>
  );
};
